import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {FormControl} from '@angular/forms';
import {from, Observable, of} from 'rxjs';
import {COMMA, ENTER, S} from '@angular/cdk/keycodes';
import {MatAutocomplete, MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {MatChipGrid} from '@angular/material/chips';
import {filter, map, startWith} from 'rxjs/operators';
import {MultiSelectEntry} from '@shared/models/multi-select-entry';

@Component({
    selector: 'hmt-multi-select-drop-down',
    templateUrl: './multi-select-drop-down.component.html',
    styleUrls: ['./multi-select-drop-down.component.scss'],
    standalone: false
})
export class MultiSelectDropDownComponent implements OnInit, AfterViewInit {

  @Input()placeholder: string;
  @Input()label: string;
  @Input('selectedItems') set setSelectedItems(res) {
    if (res) {
      this.selectedItems = res;
    }
  }


  @Input('itemList') set setItem(res: MultiSelectEntry[]) {
    if (res) {
      this.itemList = res;
      this.setFilteredItems();
    }
  }

  @Input('formControl') set setFormControl(formControl: FormControl) {
    this.formControl = formControl;
  }

  @Input() disabled = false;

  @Output() selectedItemListEvent = new EventEmitter<MultiSelectEntry[]>();

  @ViewChild('selectedItemInput') selectedItemInput: ElementRef<HTMLInputElement>;
  @ViewChild('chipGrid') chipGrid: MatChipGrid;
  @ViewChild('autoComplete') autoComplete: MatAutocomplete;

  selectedItems: MultiSelectEntry [] = [];
  itemList: MultiSelectEntry[];
  selectable = true;
  removable = true;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  filteredItems: Observable<MultiSelectEntry[]>;
  formControl = new FormControl();

  constructor() {
    // Move event listeners to ngAfterViewInit to ensure ViewChild is available
  }

  ngAfterViewInit(): void {
    // Add event listeners after view is initialized
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closePanel();
      }
    });
    
    document.addEventListener('click', (event) => {
      const clickedElement = event.target as HTMLElement;
      const isClickedInside = this.selectedItemInput?.nativeElement?.contains(clickedElement) ||
        clickedElement.closest('.mat-autocomplete-panel');
      if (!isClickedInside) {
        this.closePanel();
      }
    });
  }

  ngOnInit(): void {
    this.updateDisabledState();
  }

  setFilteredItems(): void {
    if (this.formControl) {
      this.filteredItems = this.formControl.valueChanges.pipe(
        startWith(''),
        map((value: string) => this.filterAutoSearch(value || '')));
    }
  }

  remove(item: MultiSelectEntry): void {
    const index = this.selectedItems.indexOf(item);

    if (index >= 0) {
      this.selectedItems.splice(index, 1);

      if (this.formControl) {
        this.formControl.setValue('');
      }
      this.selectedItemListEvent.emit(this.selectedItems);
    }
  }

  selectItem(event: MultiSelectEntry): void {
    this.selectedItems.push(event);
    if (this.selectedItemInput?.nativeElement) {
      this.selectedItemInput.nativeElement.value = '';
    }
    if (this.formControl) {
      this.formControl.setValue('');
    }
    this.selectedItemListEvent.emit(this.selectedItems);
    if (this.selectedItemInput?.nativeElement) {
      this.selectedItemInput.nativeElement.focus();
    }
  }

  filterAutoSearch (value: string) : MultiSelectEntry[] {
    if (!this.itemList) {
      return [];
    }
    
    const items = this.itemList.filter(item => {
      if (this.selectedItems.filter(selectedItem => selectedItem.id === item.id).length === 0){
        return item;
      }
    }); 
    if (typeof value !== 'string') {
      return items;
    }
    return items.filter(item => item.name.toLowerCase().includes(value.toLowerCase()));
  }

  closePanel(): void {
    if (this.selectedItemInput?.nativeElement) {
      this.selectedItemInput.nativeElement.blur();
    }
  }

  updateDisabledState(): void {
    if (this.disabled) {
      this.selectable = false;
      this.removable = false;
      this.formControl.disable();
    } else {
      this.selectable = true;
      this.removable = true;
      this.formControl.enable();
    }
  }
}